import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChatTriagePilotLocation,
  getChatTriagePilotLocations,
  queryKeys,
  setChatTriagePilotLocation,
  useBusyAction,
} from '@vgrubs/components';

/**
 * Whether the AI chat assistant runs for one location, and the switch for it.
 *
 * The endpoint answers with the ENABLED locations only, so membership of that
 * list is the whole state - there is nothing to look up per location, and one
 * cached list serves every location screen the user opens.
 */
export const useChatTriagePilot = (locationId: number, enabled: boolean) => {
  const queryClient = useQueryClient();

  const pilots = useQuery({
    queryKey: queryKeys.chatTriage.pilotLocations,
    queryFn: async () => {
      const { data } = await getChatTriagePilotLocations();
      return data.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const { busy, run } = useBusyAction();

  const toggle = (next: boolean) =>
    run(async () => {
      const { data } = await setChatTriagePilotLocation(locationId, next);
      // The answer is this location's row, so the list is patched rather than
      // refetched - and a location that was switched off leaves it entirely.
      queryClient.setQueryData<ChatTriagePilotLocation[]>(
        queryKeys.chatTriage.pilotLocations,
        (list) => {
          const rest = (list ?? []).filter(
            (row) => row.locationId !== locationId
          );
          return data.data.enabled ? [...rest, data.data] : rest;
        }
      );
    }, next ? 'AI chat assistant turned on.' : 'AI chat assistant turned off.');

  return {
    isOn: (pilots.data ?? []).some((row) => row.locationId === locationId),
    isLoading: pilots.isLoading,
    busy,
    toggle,
  };
};
